import FIXParser, {
	Field,
	Fields,
	Messages,
} from 'fix-over-tcp';
import { Buffer } from 'buffer';

const crypto = require('crypto-browserify');

const FIX_VERSION = 'FIX.4.4';
const TARGET_NAME = 'DERIBITSERVER';
const DEFAULT_HEARTBEAT = 30; // seconds


// TODO: Check how to handle UseWordSafeTags
const DERIBIT_RESERVED_FIELDS = [
	{ tag: 9001, name: 'CancelOnDisconnect' },
	{ tag: 9002, name: 'UseWordsafeTags' },
	{ tag: 9003, name: 'DontCancelOnDisconnect' },
	{ tag: 9004, name: 'DeribitAppId' },
	{ tag: 9005, name: 'DeribitAppSig' },
	{ tag: 9007, name: 'DeribitSequential' },
	{ tag: 9008, name: 'DeribitMMProtection' },
	{ tag: 9009, name: 'UnsubscribeExecutionReports' },
	{ tag: 9010, name: 'ConnectionOnlyExecutionReports' },
	{ tag: 9011, name: 'DeribitSkipBlockTrades' },
	{ tag: 9012, name: 'DeribitShowBlockTradeId' },
	{ tag: 9013, name: 'DisplayMulticastInstrumentID' },
	{ tag: 9014, name: 'MassStatusReqIDType' },
	{ tag: 9018, name: 'DisplayIncrementSteps' },
	{ tag: 9019, name: 'MMPGroup' },
	{ tag: 9020, name: 'QuoteEntryType' },
    { tag: 9022, name: 'MarginAmtlastBreachedSince' },
    { tag: 9023, name: 'CrossDeltaTotal' },
    { tag: 9024, name: 'CrossEquity' },
    { tag: 9025, name: 'CrossInitialMargin' },
    { tag: 9026, name: 'CrossMaintenancemargin' },
    { tag: 9027, name: 'CrossMarginBalance' },
    { tag: 9028, name: 'ProjectedCrossDelta' },
    { tag: 9029, name: 'MaxAllowedBreachTime' },
    { tag: 9030, name: 'AppendMarginBufferInfo' },
    { tag: 9031, name: 'FreezeQuotes' },
    { tag: 9032, name: 'MinDelta' },
    { tag: 9033, name: 'MaxDelta' },
    { tag: 9034, name: 'IsLiquidation' },
    { tag: 9035, name: 'IsRebalance' },
    { tag: 9036, name: 'IsRiskReducing' },
    { tag: 9037, name: 'InitialMarginScale' },
    { tag: 9038, name: 'MaintenanceMarginScale' },
    { tag: 9039, name: 'OrderHistoryIncludeUnfilled' },
    { tag: 9040, name: 'OrderHistoryOffset' },

	{ tag: 100001, name: 'DeribitUserEquity' },
	{ tag: 100002, name: 'DeribitUserBalance' },
	{ tag: 100003, name: 'DeribitUserInitialMargin' },
	{ tag: 100004, name: 'DeribitUserMaintenanceMargin' },
	{ tag: 100005, name: 'DeribitUnrealizedPl' },
	{ tag: 100006, name: 'DeribitRealizedPl' },
	{ tag: 100007, name: 'DeribitTradeAmount' },
	{ tag: 100008, name: 'DeribitSinceTimestamp' },
	{ tag: 100009, name: 'DeribitTradeId' },
	{ tag: 100010, name: 'DeribitLabel' },
	{ tag: 100011, name: 'DeribitTotalPl' },
	{ tag: 100012, name: 'DeribitAdvOrderType' },
	{ tag: 100013, name: 'DeribitMarginBalance' },
	{ tag: 100087, name: 'TradeVolume24h' },
	{ tag: 100088, name: 'DeribitLiquidationPrice' },
	{ tag: 100089, name: 'DeribitSizeInCurrency' },
	{ tag: 100090, name: 'MarkPrice' },
	{ tag: 100091, name: 'DeribitLiquidation' },
	{ tag: 100092, name: 'CurrentFunding' },
	{ tag: 100093, name: 'Funding8h' },
];

const SYSTEM_FIELDS_TAGS = [
	Fields.MsgSeqNum,
	Fields.SendingTime,
];

const COMMON_FIELDS_TAGS = [
	Fields.MsgType,
	Fields.SenderCompID,
	Fields.TargetCompID,
];

const DEFAULT_FIELD_TAGS = [
		...SYSTEM_FIELDS_TAGS,
		...COMMON_FIELDS_TAGS,
];

function calculateRawDataString() {
	const timestampInMs = new Date().getTime();
	const nonceInBase64 = Buffer.from(crypto.randomBytes(32)).toString('base64');
	return [timestampInMs, nonceInBase64].join('.');
}

function calculatePassword(apiSecret, rawDataString) {
	const baseSignatureString = rawDataString + apiSecret;
	return Buffer.from(crypto.createHash('sha256').update(baseSignatureString).digest()).toString('base64');
}

export function calculateLoginParams(clientId, clientSecret) {
	const rawDataString = calculateRawDataString();

	return [
		{ tag: Fields.Username, value: clientId },
		{ tag: Fields.Password, value: calculatePassword(clientSecret, rawDataString) },
		{ tag: Fields.HeartBtInt, value: DEFAULT_HEARTBEAT },
		{ tag: Fields.RawData, value: rawDataString },
	];
}

export class FixClient {
	constructor(config) {
		this.serverUrl = config.serverUrl;
		this.messageHandler = config.onMessage;
		this.connectHandler = config.onConnect;
		this.disconnectHandler = config.onDisconnect;
		this.errorHandler = config.onError;

		this.fixParser = new FIXParser();
		this.fixParser.fixVersion = FIX_VERSION;
		this.senderId = `CLIENT_${String(Math.random()).split('.')[1]}`;
		this.messageCounter = 1;
	}

	connect() {
		if (this.isOpen() || this.isConnecting()) {
			this.log(`[FixClient] - connect() called while already in state "${this.connectionState()}". Ignoring...`);
			return;
		}

		this.log('[FixClient] - Connecting...');
		this.socket = new WebSocket(this.serverUrl);

		this.socket.onopen =  () => this.onConnect();
		this.socket.onclose = (msg) => this.onDisconnect(msg);
		this.socket.onmessage = (msg) => this.onMessage(msg);
		this.socket.onerror = (error) => this.onError(error);
	}

	onConnect() {
		this.log('[FixClient] - Connection success');

		const { messageData, onSuccess } = this.bufferedMessage;
		this.send(messageData, onSuccess);

		if (this.connectHandler) {
			this.connectHandler();
		}
	}

	disconnect() {
		if (this.isClosed() || this.isClosing()) {
			this.log(`[FixClient] - Called disconnect while connection is in state: "${this.connectionState()}".`);
			return;
		}

		this.socket.close(1000, 'Client closed');
	}

	onDisconnect(message) {
		this.log('[FixClient] - Disconnected', message);
		this.messageCounter = 1;

		if (this.disconnectHandler) {
			this.disconnectHandler(message);
		}
	}

	async onMessage(message) {
		const text = await message.data?.text();
		this.log('[FixClient] - Received Message', text);

		const messagesData = this.parseRawFix(text);

		if (messagesData.some(message => message.type === Messages.Logout)) {
			this.disconnect();
		}

		if (this.messageHandler) {
			messagesData.forEach(messageData => this.messageHandler(messageData));
		}
	}

	onError(error) {
		if (this.errorHandler) {
			this.errorHandler(error);
		}
	}

	setServerUrl(serverUrl) {
		this.disconnect();
		this.serverUrl = serverUrl;
	}

	getDetailedMessage(messageString) {
		const [parsed] = this.fixParser.parse(messageString);

		if (!parsed) {
			return undefined;
		}

		return this.getDetailedMessageData(parsed);
	}

	parseRawFix(message) {
		const parsedMessages = this.fixParser.parse(message);

		if (!parsedMessages?.length) {
			return undefined;
		}

		return parsedMessages.map(parsed => this.getDetailedMessageData(parsed));
	}

	getDetailedMessageData(parsedFix) {
		const parts = parsedFix.data.map(({ tag, value, name, description, enumeration }) => {
			const reservedField = DERIBIT_RESERVED_FIELDS.find(field => field.tag === tag);
			return reservedField
				? { tag, value, name: reservedField.name }
				: { tag, value, name, description, valueName: enumeration?.symbolicName };
		});

		const type = parsedFix.messageType || parsedFix.data.find(el => el.tag === Fields.MsgType)?.value;

		return {
			type,
			fixString: parsedFix.string,
			name: parsedFix.description || type,
			parts,
		};
	}


	send(messageData, onSuccess) {
		if (!messageData || !messageData.type) {
			this.log('[FixClient] - Nothing to send');
			return;
		}

		if (!this.isOpen()) {
			this.log(`[FixClient] - Attempting to send message "${messageData.type}" while connection state is: "${this.connectionState()}".`);

			// If we are trying to send logon, start connection
			if (messageData.type === Messages.Logon) {
				this.bufferedMessage = { messageData, onSuccess };
				this.connect();
			}

			return;
		}

		this.sendMessage(messageData, onSuccess);
	}

	ensureCorrectBodyLength(fixString) {
		const [currentBodyLengthTag, libraryCalculatedLength] = fixString.match(/(?<!\d)9=(\d+)/);

		const startIndex = fixString.search(/(?<!\d)35=/);
		const endIndex = fixString.search(/(?<!\d)10=/);

		const bodyLength = endIndex - startIndex;

		if (bodyLength !== Number(libraryCalculatedLength)) {
			this.log(`[FixClient] - Library calculated bodyLenght incorrectly (${libraryCalculatedLength}). Overwriting with "${bodyLength}"`);
		}

		return fixString.replace(currentBodyLengthTag, `9=${ bodyLength }`);
	}

	sendMessage(messageData, onSuccess) {
		const message = this.composeMessage(messageData);
		const fixString = this.ensureCorrectBodyLength(message.encode().toString());
		this.log('SENDING => ', fixString);
		this.messageCounter += 1;
		this.socket.send(new TextEncoder().encode(fixString));

		if (onSuccess) {
			onSuccess(fixString);
		}
	}

	getLoginMessage(clientId, clientSecret) {
		return { type: Messages.Logon, parts: calculateLoginParams(clientId, clientSecret) };
	}

	isHeartbeatMessage(messageType) {
		return messageType === Messages.Heartbeat;
	}

	getMessageFromRawFix(fixString) {
		const [parsedMessage] = this.parseRawFix(fixString);

		const errors = this.validateFix(parsedMessage);
		if (errors.length) {
			return { errors };
		}

		const message = {
			...parsedMessage,
			parts: parsedMessage.parts,
		};

		return { message };
	}

	validateFix(parsedFix) {
		if (!parsedFix) {
			return [{
				message: 'Invalid format',
			}];
		}

		const errors = [];
		const protocolTag = parsedFix.parts.find(part => part.tag === Fields.BeginString);
		if (protocolTag?.value !== FIX_VERSION) {
			errors.push({
				message: 'Invalid BeginString (8)',
				expected: FIX_VERSION,
				received: protocolTag.value,
				tag: Fields.BeginString,
			});
		}

		const targetCompId = parsedFix.parts.find(part => part.tag === Fields.TargetCompID);
		if (targetCompId?.value !== TARGET_NAME) {
			errors.push({
				message: 'Invalid TargetCompId (56)',
				expected: TARGET_NAME,
				received: targetCompId.value,
				tag: Fields.TargetCompID,
			});
		}

		return errors;
	}

	composeMessage(data) {
		const { type, parts } = data;

		const systemFields = SYSTEM_FIELDS_TAGS.map(tag => {
			switch (tag) {
				case Fields.MsgSeqNum:
					return new Field(tag, this.messageCounter);
				case Fields.SendingTime:
					return new Field(tag, this.fixParser.getTimestamp());
			}
		});

		const commonFields = COMMON_FIELDS_TAGS.map(tag => {
			switch (tag) {
				case Fields.MsgType:
					return new Field(tag, type);
				case Fields.SenderCompID:
					return new Field(tag, this.senderId);
				case Fields.TargetCompID:
					return new Field(Fields.TargetCompID, TARGET_NAME);
			}
		});

		const messageFields = parts?.map(part => new Field(part.tag, part.value));

		if (messageFields) {
			const allowedUserFields = messageFields
				.filter(field => !SYSTEM_FIELDS_TAGS.includes(field.tag));

			const commons = commonFields.map(commonField => {
				const userDefinedCommon = allowedUserFields.find(userField => commonField.tag === userField.tag);
				return userDefinedCommon ? userDefinedCommon : commonField;
			});

			const userMessageFields = allowedUserFields.filter(field => !COMMON_FIELDS_TAGS.includes(field.tag));

			return this.fixParser.createMessage(
				...commons,
				...systemFields,
				...userMessageFields,
			);
		} else {
			return this.fixParser.createMessage(...commonFields, ...systemFields);
		}
	}

	log(message, data) {
		if (process.env.NODE_ENV === 'production') {
			return;
		}

		console.log(message, data || '');
	}

	isClosed() {
		return !this.socket || this.socket.readyState === WebSocket.CLOSED;
	}

	isClosing() {
		return this.socket && this.socket.readyState === WebSocket.CLOSING;
	}

	isOpen() {
		return this.socket && this.socket.readyState === WebSocket.OPEN;
	}

	isConnecting() {
		return this.socket && this.socket.readyState === WebSocket.CONNECTING;
	}

	connectionState() {
		if (!this.socket) {
			return 'CLOSED';
		}

		switch (this.socket.readyState) {
			case WebSocket.OPEN:
				return 'OPEN';
			case WebSocket.CONNECTING:
				return 'CONNECTING';
			case WebSocket.CLOSED:
				return 'CLOSED';
			case WebSocket.CLOSING:
				return 'CLOSING';
		}

	}
}
